import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { TableModule, TableLazyLoadEvent, Table } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { IftaLabelModule } from 'primeng/iftalabel';
import { EntityDropdownPickerComponent } from '../../../../shared/components/entity-dropdown-picker/entity-dropdown-picker.component';
import { EntityDropdownPickerAdapter } from '../../../../shared/components/entity-dropdown-picker/entity-dropdown-picker.model';
import { SubBrandsService } from '../sub-brands.service';
import { SubBrand } from '../sub-brands.model';
import { Page } from '../../../../models/page-wrapper.model';
import * as XLSX from 'xlsx';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import { BrandsService } from '../../brands.service';
import { Brand } from '../../brands.model';

@Component({
  selector: 'app-sub-brands-list',
  templateUrl: './sub-brands-list.component.html',
  styleUrl: './sub-brands-list.component.css',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TableModule,
    ButtonModule,
    ToastModule,
    ConfirmDialogModule,
    RouterModule,
    InputGroupModule,
    InputGroupAddonModule,
    ReactiveFormsModule,
    InputTextModule,
    IftaLabelModule,
    EntityDropdownPickerComponent,
  ],
  providers: [ConfirmationService, MessageService],
})
export class SubBrandsListComponent implements OnInit {
  @ViewChild('dt') dt!: Table;
  subBrands: SubBrand[] = [];
  totalRecords = 0;
  isLoading = false;
  filterForm!: FormGroup;
  filterSub!: Subscription;
  brandPickerAdapter!: EntityDropdownPickerAdapter<Brand>;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private subBrandsService: SubBrandsService,
    private brandsService: BrandsService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.buildFilterForm();
    this.brandPickerAdapter = this.brandsService.createBrandPickerAdapter();
    this.filterSub = this.filterForm.valueChanges
      .pipe(
        debounceTime(500), // Wait for 500ms of inactivity
        distinctUntilChanged() // Only emit if the value has changed
      )
      .subscribe(() => {
        // Reset paginator to the first page and trigger a reload
        this.dt.reset();
      });
    // this.loadSubBrands();
  }

  buildFilterForm() {
    this.filterForm = this.fb.group({
      subBrandName: [''],
      subBrandCode: [''],
      brand: [null],
    });
  }

  onSearch(): void {
    this.dt.reset();
  }

  onReset(): void {
    this.filterForm.reset({
      subBrandName: '',
      subBrandCode: '',
      brand: null,
    });
    this.dt.reset();
  }

  ngOnDestroy(): void {
    this.filterSub?.unsubscribe();
  }

  loadSubBrands(
    event?: TableLazyLoadEvent,
    isExport: boolean = false,
    callback?: (subBrands: SubBrand[]) => void
  ): void {
    this.isLoading = true;
    let params: any;

    if (isExport) {
      params = {
        page: 0,
        size: 2000,
        subBrandName: this.filterForm.value.subBrandName,
        subBrandCode: this.filterForm.value.subBrandCode,
        'brand.id': this.filterForm.value.brand?.id,
      };
    } else {
      const size = event?.rows ?? 10;
      const page = event?.first ? Math.floor(event.first / size) : 0;
      params = {
        page,
        size,
        subBrandName: this.filterForm.value.subBrandName,
        subBrandCode: this.filterForm.value.subBrandCode,
        'brand.id': this.filterForm.value.brand?.id,
      };
    }

    Object.keys(params).forEach((key) => {
      if (!params[key]) {
        delete params[key];
      }
    });

    this.subBrandsService.getAllSubBrands(params).subscribe({
      next: (page: Page<SubBrand>) => {
        this.subBrands = page.content;
        this.totalRecords = page.totalElements;
        this.isLoading = false;
        if (callback) {
          callback(page.content);
        }
      },
      error: () => {
        this.isLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load Sub Brands.',
        });
      },
    });
  }

  deleteSubBrand(id: number): void {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this Sub Brand?',
      header: 'Confirm Deletion',
      icon: 'pi pi-exclamation-triangle',

      acceptLabel: 'Yes, Delete',
      rejectLabel: 'Cancel',

      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-success',

      accept: () => {
        this.subBrandsService.deleteSubBrand(id).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: `Sub Brand Deleted successfully.`,
            });
            setTimeout(() => {
              this.loadSubBrands();
            }, 500);
          },
          error: (err) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: `Failed to Delete Sub Brand.`,
            });
            console.error(err);
          },
        });
      },
    });
  }

  createSubBrand() {
    this.router.navigate(
      ['.', { outlets: { canvasOutlet: ['sub-brand', 'create'] } }],
      { relativeTo: this.route.parent?.parent }
    );
  }

  editSubBrand(id: number): void {
    this.router.navigate(
      ['.', { outlets: { canvasOutlet: ['sub-brand', 'edit', id] } }],
      { relativeTo: this.route.parent?.parent }
    );
  }

  exportExcel(): void {
    this.loadSubBrands(undefined, true, (subBrands: SubBrand[]) => {
      const dataToExport = subBrands.map((subBrand) => ({
        'Sub Brand Name': subBrand.subBrandName,
        'Sub Brand Code': subBrand.subBrandCode,
        Brand: subBrand.brand?.brandName ?? '-',
      }));

      const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(dataToExport);
      const wb: XLSX.WorkBook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'sub-brands');

      XLSX.writeFile(wb, 'Sub_Brands_Export.xlsx');
    });
  }
}
